<template>
  <div>
    <breadcrumbs :breadcrumb-items="breadcrumbItems" />

    <UserToolbar
      v-if="account && Object.keys(account).length !== 0 && shouldShowToolbar()"
      :account="account"
    />

    <b-card v-if="adTitle">
      <GoogleAdsToolbar :title="adTitle" />
    </b-card>

    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div
            v-if="textVariantCount"
            class="head-card-bottom mt-2"
            role="tablist"
          >
            <b-button
              v-for="variant in textVariants"
              :key="variant"
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              :class="{ 'router-link-active': variant === selectedTextVariant }"
              variant="flat-primary"
              pill
              role="tab"
              :aria-selected="variant === selectedTextVariant ? 'true' : 'false'"
              @click="selectTextVariant(variant)"
            >
              {{ textVariantLabel(variant) }}
            </b-button>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              v-model="rangePicker"
              :is-loading="loading"
              class="offerwall-events-filters"
              @range-change="toggleRange"
              @refresh="fetchEvents"
            />
          </div>
        </div>
      </div>

      <div
        v-if="loading"
        class="d-flex justify-content-center mb-1"
      >
        <b-spinner :label="$t('offerwall_events.loading')" />
      </div>

      <b-alert
        v-else-if="hasError"
        show
        variant="danger"
        class="mb-0"
      >
        {{ $t('offerwall_events.load_error') }}
      </b-alert>

      <b-alert
        v-else-if="!eventRows.length"
        show
        variant="light"
        class="mb-0 text-center"
      >
        {{ $t('offerwall_events.empty') }}
      </b-alert>

      <div
        v-else
        class="events-table-scroll"
      >
        <b-table
          small
          :items="eventRows"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          striped
          foot-clone
          class="events-table"
        >
          <template #head(date)>
            {{ $t('message.tableHeader.date') }}
          </template>

          <template #head(loaded)>
            {{ $t('offerwall_events.types.loaded') }}
          </template>

          <template #head(viewed)>
            {{ $t('offerwall_events.types.viewed') }}
          </template>

          <template #head(closed)>
            {{ $t('offerwall_events.types.closed') }}
          </template>

          <template #head(leftPage)>
            {{ $t('offerwall_events.left_page') }}
          </template>

          <template #cell(date)="row">
            <a
              href="#"
              :class="row.item._showDetails === true ? 'text-secondary' : ''"
              :aria-expanded="row.item._showDetails === true ? 'true' : 'false'"
              @click.prevent="toggleHourlyDetails(row)"
            >
              <strong>{{ formatDate(row.item.date) }}</strong>
              <feather-icon
                class="ml-15"
                :icon="row.item._showDetails === true ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                size="20"
              />
            </a>
          </template>

          <template #cell(loaded)="data">
            {{ formatCount(data.value) }}
          </template>

          <template #cell(viewed)="data">
            {{ formatCount(data.value) }}
          </template>

          <template #cell(closed)="data">
            {{ formatCount(data.value) }}
          </template>

          <template #cell(leftPage)="data">
            {{ formatCount(data.value) }}
          </template>

          <template #row-details="{ item }">
            <div class="hourly-details">
              <div
                v-if="item._hourlyLoading"
                class="d-flex justify-content-center py-2"
              >
                <b-spinner small />
              </div>
              <b-alert
                v-else-if="item._hourlyError"
                show
                variant="danger"
                class="m-1"
              >
                {{ $t('offerwall_events.hourly_error') }}
              </b-alert>
              <b-table
                v-else
                small
                :items="item._hourlyRows || []"
                :fields="hourlyFields"
                thead-class="events-hourly-head"
                class="mb-0 hourly-events-table"
              >
                <template #head(hour)>
                  {{ $t('offerwall_events.hour') }}
                </template>
                <template #head(loaded)>
                  {{ $t('offerwall_events.types.loaded') }}
                </template>
                <template #head(viewed)>
                  {{ $t('offerwall_events.types.viewed') }}
                </template>
                <template #head(closed)>
                  {{ $t('offerwall_events.types.closed') }}
                </template>
                <template #head(leftPage)>
                  {{ $t('offerwall_events.left_page') }}
                </template>
                <template #cell(loaded)="data">
                  {{ formatCount(data.value) }}
                </template>
                <template #cell(viewed)="data">
                  {{ formatCount(data.value) }}
                </template>
                <template #cell(closed)="data">
                  {{ formatCount(data.value) }}
                </template>
                <template #cell(leftPage)="data">
                  {{ formatCount(data.value) }}
                </template>
                <template #empty>
                  <div class="text-center py-1">
                    {{ $t('offerwall_events.empty_hourly') }}
                  </div>
                </template>
              </b-table>
            </div>
          </template>

          <template #foot(date)>
            <strong>{{ $t('Total') }}</strong>
          </template>

          <template #foot(loaded)>
            <strong>{{ formatCount(eventTotals.loaded) }}</strong>
          </template>

          <template #foot(viewed)>
            <strong>{{ formatCount(eventTotals.viewed) }}</strong>
          </template>

          <template #foot(closed)>
            <strong>{{ formatCount(eventTotals.closed) }}</strong>
          </template>

          <template #foot(leftPage)>
            <strong>{{ formatCount(eventTotals.loaded - eventTotals.viewed) }}</strong>
          </template>
        </b-table>
      </div>
    </b-card>
  </div>
</template>

<script>
import { isOfferWallOperator } from '@/auth/roles'
import {
  BAlert,
  BButton,
  BCard,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useJwt from '@/auth/jwt/useJwt'
import { getUserData } from '@/auth/utils'
import getFormattedDate from '@/utils/date-formatter'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import UserToolbar from '@/views/dashboard/users/UserToolbar.vue'
import StatsDateRangeFilter from '@/views/dashboard/partners/stats/StatsDateRangeFilter.vue'
import GoogleAdsToolbar from './GoogleAdsToolbar.vue'

const EVENT_TYPES = ['loaded', 'viewed', 'closed']

function isOfferWallOperatorUser() {
  return isOfferWallOperator(getUserData())
}

export default {
  name: 'GoogleAdsEvents',
  components: {
    BAlert,
    BButton,
    BCard,
    BSpinner,
    BTable,
    Breadcrumbs,
    GoogleAdsToolbar,
    StatsDateRangeFilter,
    UserToolbar,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      account: {},
      adTitle: '',
      breadcrumbItems: [],
      dailyEvents: [],
      textVariantCount: 0,
      selectedTextVariant: -1,
      eventsRequestId: 0,
      hasError: false,
      loading: false,
      sortBy: 'date',
      sortDesc: true,
      totalsFromApi: null,
      rangePicker: [
        this.$route.query.from || getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        this.$route.query.to || getFormattedDate(new Date()),
      ],
    }
  },
  computed: {
    textVariants() {
      return [-1, ...Array.from({ length: this.textVariantCount + 1 }, (_, index) => index)]
    },
    selectedTargetIds() {
      const { adId } = this.$route.params
      const variants = this.selectedTextVariant === -1
        ? this.textVariants.slice(1)
        : [this.selectedTextVariant]
      return variants.map(variant => (variant === 0 ? adId : `${adId}-${variant}`))
    },
    fields() {
      return [
        {
          key: 'date',
          label: 'Date',
          thClass: 'event-period-column',
          tdClass: 'event-period-column',
        },
        ...EVENT_TYPES.map(type => ({
          key: type,
          label: type,
          thClass: 'text-right',
          tdClass: 'text-right',
        })),
        {
          key: 'leftPage',
          label: 'Left page',
          thClass: 'text-right',
          tdClass: 'text-right',
        },
      ]
    },
    hourlyFields() {
      return [
        {
          key: 'hour',
          label: 'Hour',
          thClass: 'event-period-column',
          tdClass: 'event-period-column',
        },
        ...EVENT_TYPES.map(type => ({
          key: type,
          label: type,
          thClass: 'text-right',
          tdClass: 'text-right',
        })),
        {
          key: 'leftPage',
          label: 'Left page',
          thClass: 'text-right',
          tdClass: 'text-right',
        },
      ]
    },
    eventRows() {
      return this.pivotEvents(this.dailyEvents, 'date')
        .sort((a, b) => String(b.date).localeCompare(String(a.date)))
    },
    eventTotals() {
      if (this.totalsFromApi) return this.totalsFromApi
      return this.eventRows.reduce((totals, row) => {
        EVENT_TYPES.forEach(type => {
          totals[type] += this.toNumber(row[type])
        })
        return totals
      }, this.emptyCounts())
    },
  },
  created() {
    if (this.$route.query.event_type) {
      const query = { ...this.$route.query }
      delete query.event_type
      this.$router.replace({ name: 'dashboard-google-ads-events', query }).catch(error => {
        if (error.name !== 'NavigationDuplicated') throw error
      })
    }
    this.loadData()
  },
  methods: {
    textVariantLabel(variant) {
      if (variant === -1) return this.$t('offerwall_events.all_texts')
      if (variant === 0) return this.$t('offerwall_events.primary_text')
      return this.$t('google_ads.texts_options_variant', { number: variant })
    },
    shouldShowToolbar() {
      if (!isOfferWallOperatorUser()) return true
      const currentUser = getUserData()
      return String(this.$route.params.userId) === String(currentUser?.id)
    },
    async loadData() {
      this.loading = true
      const { userId, adId } = this.$route.params
      let userName = ''
      let adName = adId

      try {
        const userResponse = await useJwt.getUser(userId)
        this.account = userResponse.data.account || {}
        userName = this.account.name || this.account.email || ''
      } catch (error) {
        this.account = {}
      }

      try {
        const adResponse = await useJwt.getGoogleWidget(adId)
        const widget = adResponse.data.googlewidget || {}
        adName = widget.name || widget.title || adId
        this.textVariantCount = Array.isArray(widget.texts_options) ? widget.texts_options.length : 0
      } catch (error) { /* Use the OfferWall ID as a safe fallback. */ }

      this.adTitle = adName
      this.setBreadcrumbs(userId, userName, adName)
      this.fetchEvents()
    },
    setBreadcrumbs(userId, userName, adName) {
      const items = userName
        ? [
          { text: 'users', to: '/users/' },
          { text: userName, to: `/users/${userId}/offerwall` },
          { text: adName, to: `/users/${userId}/offerwall` },
        ]
        : [
          { text: 'OfferWall', to: '/offerwall' },
          { text: adName, to: `/users/${userId}/offerwall` },
        ]
      this.breadcrumbItems = [
        ...items,
        { text: this.$t('offerwall_events.title'), active: true },
      ]
    },
    async fetchEvents() {
      this.eventsRequestId += 1
      const requestId = this.eventsRequestId
      if (!this.rangePicker || this.rangePicker.length < 2) {
        this.dailyEvents = []
        this.totalsFromApi = null
        this.loading = false
        return
      }

      this.loading = true
      this.hasError = false
      this.totalsFromApi = null
      const params = {
        type: 'daily',
        from: this.rangePicker[0],
        to: this.rangePicker[1],
      }

      try {
        const responses = await Promise.all(this.selectedTargetIds.map(async targetId => {
          const targetParams = { ...params, target_id: targetId }
          const [eventsResponse, totalResponse] = await Promise.all([
            useJwt.getWidgetEvents(targetParams),
            useJwt.getWidgetEventsTotal(targetParams).catch(() => null),
          ])
          return {
            rows: this.extractRows(eventsResponse.data),
            totals: totalResponse ? this.normalizeTotals(totalResponse.data) : null,
          }
        }))
        if (requestId !== this.eventsRequestId) return
        this.dailyEvents = responses.flatMap(response => response.rows)
        if (responses.every(response => response.totals)) {
          this.totalsFromApi = responses.reduce((totals, response) => {
            EVENT_TYPES.forEach(type => {
              totals[type] += response.totals[type]
            })
            return totals
          }, this.emptyCounts())
        }
      } catch (error) {
        if (requestId !== this.eventsRequestId) return
        this.hasError = true
        this.dailyEvents = []
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('offerwall_events.load_error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        if (requestId === this.eventsRequestId) this.loading = false
      }
    },
    selectTextVariant(variant) {
      if (variant === this.selectedTextVariant) return
      this.selectedTextVariant = variant
      this.dailyEvents = []
      this.totalsFromApi = null
      this.fetchEvents()
    },
    toggleRange(range) {
      this.rangePicker = range
      this.updateRouteQuery()
      this.fetchEvents()
    },
    updateRouteQuery() {
      const query = {
        from: this.rangePicker[0],
        to: this.rangePicker[1],
      }
      this.$router.replace({
        name: 'dashboard-google-ads-events',
        query,
      }).catch(error => {
        if (error.name !== 'NavigationDuplicated') throw error
      })
    },
    async toggleHourlyDetails(row) {
      if (row.item._showDetails) {
        row.toggleDetails()
        return
      }

      row.toggleDetails()
      if (row.item._hourlyLoaded || row.item._hourlyLoading) return
      this.$set(row.item, '_hourlyLoading', true)
      this.$set(row.item, '_hourlyError', false)
      const params = {
        type: 'hourly',
        date: row.item.date,
      }

      try {
        const responses = await Promise.all(this.selectedTargetIds.map(targetId => useJwt.getWidgetEvents({
          ...params,
          target_id: targetId,
        })))
        const hourlyRows = this.pivotEvents(responses.flatMap(response => this.extractRows(response.data)), 'hour')
          .sort((a, b) => String(a.hour).localeCompare(String(b.hour)))
        this.$set(row.item, '_hourlyRows', hourlyRows)
        this.$set(row.item, '_hourlyLoaded', true)
      } catch (error) {
        this.$set(row.item, '_hourlyError', true)
      } finally {
        this.$set(row.item, '_hourlyLoading', false)
      }
    },
    extractRows(payload) {
      if (Array.isArray(payload)) return payload
      if (!payload || typeof payload !== 'object') return []
      const keys = [
        'widget_events',
        'widgetevents',
        'widgetEvents',
        'events',
        'stats',
        'items',
        'data',
        'results',
        'total',
      ]
      const rows = keys.map(key => payload[key]).find(Array.isArray)
      return rows || []
    },
    normalizeTotals(payload) {
      const totals = this.emptyCounts()
      const rows = this.extractRows(payload)
      if (rows.length) {
        rows.forEach(item => this.addItemCounts(totals, item))
        return totals
      }
      if (!payload || typeof payload !== 'object') return null
      const source = payload.totals || payload.total || payload
      if (typeof source === 'number') {
        const eventType = String(payload.event_type || payload.eventType || '').toLowerCase()
        if (EVENT_TYPES.includes(eventType)) {
          totals[eventType] = source
          return totals
        }
      }
      if (source && typeof source === 'object') {
        EVENT_TYPES.forEach(type => {
          totals[type] = this.toNumber(source[type])
        })
        if (EVENT_TYPES.some(type => totals[type] !== 0)) return totals
      }
      return null
    },
    pivotEvents(rows, periodKey) {
      const grouped = new Map()
      rows.forEach(item => {
        const period = this.getPeriod(item, periodKey)
        if (period === '') return
        if (!grouped.has(period)) {
          grouped.set(period, {
            [periodKey]: period,
            ...this.emptyCounts(),
          })
        }
        this.addItemCounts(grouped.get(period), item)
      })
      return Array.from(grouped.values()).map(row => ({
        ...row,
        leftPage: row.loaded - row.viewed,
      }))
    },
    addItemCounts(target, item) {
      const eventType = String(
        item.event_type
        || item.eventType
        || item.event
        || item._id?.event_type
        || item._id?.eventType
        || '',
      ).toLowerCase()
      if (EVENT_TYPES.includes(eventType)) {
        target[eventType] += this.getCount(item)
        return
      }
      EVENT_TYPES.forEach(type => {
        target[type] += this.toNumber(item[type])
      })
    },
    getPeriod(item, periodKey) {
      if (periodKey === 'date') {
        const value = item.date
          || item.day
          || item.period
          || item.datetime
          || item.timestamp
          || item._id?.date
          || item._id?.day
        return value ? String(value).slice(0, 10) : ''
      }
      const value = item.hour
        ?? item.period
        ?? item.datetime
        ?? item.timestamp
        ?? item.date
        ?? item._id?.hour
      if (value === undefined || value === null) return ''
      const text = String(value)
      const matchedHour = text.match(/(?:T|\s)(\d{2})(?::|$)/)
      if (matchedHour) return `${matchedHour[1]}:00`
      if (/^\d{1,2}$/.test(text)) return `${text.padStart(2, '0')}:00`
      return text
    },
    getCount(item) {
      const value = item.count
        ?? item.total
        ?? item.value
        ?? item.events
        ?? item.events_count
        ?? item.event_count
      return this.toNumber(value)
    },
    emptyCounts() {
      return { loaded: 0, viewed: 0, closed: 0 }
    },
    toNumber(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    formatCount(value) {
      return new Intl.NumberFormat('en-GB').format(this.toNumber(value))
    },
    formatDate(value) {
      if (!value) return ''
      return getFormattedDate(new Date(`${String(value).slice(0, 10)}T00:00:00`))
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.events-table-scroll {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

[dir] .events-table.table,
[dir] .hourly-events-table.table {
  width: 100%;
  min-width: 680px;
  table-layout: fixed;
}

[dir] .events-table .event-period-column,
[dir] .hourly-events-table .event-period-column {
  width: 200px;
  white-space: nowrap;
}

[dir] .table.b-table > tbody > tr.b-table-details > td {
  padding: 0 !important;
}

[dir] .hourly-events-table.table {
  margin-bottom: 0;
  background: #fff;
}

[dir] .hourly-events-table.table tbody td {
  border-top-color: #ebe9f1;
  background: #ebebeb;
  vertical-align: middle;
}

@media (max-width: 767.98px) {
  .offerwall-events-filters > .d-flex {
    flex-wrap: wrap;
  }
}
</style>
